import { UPGRADE_SOURCE_ELEMENTS, UPGRADE_SOURCE_PAGES, UPGRADE_TRIGGERS } from '@shared/core/analytics'
import type { UpgradeSourcePage, UpgradeTrigger } from '@shared/core/analytics'
import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import type { RemindersPausedReason } from '@shared/core/billing'
import { routeNames } from 'src/modules/core/constants/routeNames'
import type { UpgradeFunnelParams } from 'src/modules/payment/types/upgrade-funnel.types'
import type { SettingsTabName } from 'src/modules/user/containers/settings-info-wrapper/settings-info-wrapper.domain'

export const SETTINGS_TAB_SEARCH_PARAM = 'tab'
export const SETTINGS_PLANS_TAB_NAME: SettingsTabName = 'plan'

const UPGRADE_FUNNEL_PARAM_KEYS = [
  'trigger',
  'source_element',
  'source_page',
] as const satisfies readonly (keyof UpgradeFunnelParams)[]

// Payment-paused CTAs open the card update, never the plans page, so they carry no trigger.
export const UPGRADE_TRIGGER_BY_PAUSED_REASON: Record<RemindersPausedReason, UpgradeTrigger | undefined> = {
  [REMINDERS_PAUSED_REASONS.quota]: 'quota_exhausted',
  [REMINDERS_PAUSED_REASONS.subscriptionEnded]: 'subscription_ended',
  [REMINDERS_PAUSED_REASONS.paymentPaused]: undefined,
}

const SOURCE_PAGE_BY_SETTINGS_TAB: Partial<Record<SettingsTabName, UpgradeSourcePage>> = {
  calendars: 'calendars_tab',
  'booking-page': 'booking_settings',
}

export function parseUpgradeFunnelParams(searchParams: URLSearchParams): UpgradeFunnelParams {
  return {
    trigger: pickAllowedValue(searchParams.get('trigger'), UPGRADE_TRIGGERS),
    source_element: pickAllowedValue(searchParams.get('source_element'), UPGRADE_SOURCE_ELEMENTS),
    source_page: pickAllowedValue(searchParams.get('source_page'), UPGRADE_SOURCE_PAGES),
  }
}

export function buildUpgradePlansPath(params: UpgradeFunnelParams): string {
  const query = new URLSearchParams()

  UPGRADE_FUNNEL_PARAM_KEYS.forEach((key) => {
    if (params[key]) {
      query.set(key, params[key])
    }
  })

  const search = query.toString()

  return search ? `${routeNames.subscriptionPlans.path}?${search}` : routeNames.subscriptionPlans.path
}

export function resolveUpgradeSourcePage(pathname: string, searchParams: URLSearchParams): UpgradeSourcePage {
  if (pathname === routeNames.dashboard.path) {
    return 'dashboard'
  }

  if (pathname !== routeNames.settings.path) {
    return 'other'
  }

  const tabName = searchParams.get(SETTINGS_TAB_SEARCH_PARAM) as SettingsTabName

  return SOURCE_PAGE_BY_SETTINGS_TAB[tabName] ?? 'settings'
}

export function isUpgradePlansView(pathname: string, searchParams: URLSearchParams): boolean {
  // Beta accounts are redirected to their own plans page, so a plans link there would bounce back.
  if (pathname === routeNames.subscriptionPlans.path || pathname === routeNames.betaUsersSubscription.path) {
    return true
  }

  return (
    pathname === routeNames.settings.path && searchParams.get(SETTINGS_TAB_SEARCH_PARAM) === SETTINGS_PLANS_TAB_NAME
  )
}

function pickAllowedValue<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return allowed.find((candidate) => candidate === value)
}
